"use client";

import { SmilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { EMOJI_REACTIONS } from "@/lib/constants/reactions";
import { cn } from "@/lib/utils";

interface EmojiPickerProps {
	label: string;
	onSelect: (emoji: string) => void;
	disabled?: boolean;
	align?: "start" | "center" | "end";
	className?: string;
	triggerClassName?: string;
	triggerLabel?: string;
	selectedEmojis?: string[];
}

export function EmojiPicker({
	label,
	onSelect,
	disabled = false,
	align = "start",
	className,
	triggerClassName,
	triggerLabel,
	selectedEmojis = [],
}: EmojiPickerProps) {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					disabled={disabled}
					className={cn("h-8 gap-1.5 px-2", triggerClassName)}
					aria-label={label}
				>
					<SmilePlus className="size-4" strokeWidth={1.5} aria-hidden />
					{triggerLabel ? <span>{triggerLabel}</span> : null}
				</Button>
			</PopoverTrigger>
			<PopoverContent align={align} className={cn("w-auto p-2", className)}>
				<div
					className="grid grid-cols-5 gap-1"
					role="listbox"
					aria-label={label}
				>
					{EMOJI_REACTIONS.map(({ emoji, label: emojiLabel }) => {
						const selected = selectedEmojis.includes(emoji);
						return (
							<button
								key={emoji}
								type="button"
								onClick={() => onSelect(emoji)}
								className={cn(
									"flex size-9 items-center justify-center rounded-md text-lg transition-colors hover:bg-black/5 dark:hover:bg-white/8",
									selected && "bg-black/8 dark:bg-white/12",
								)}
								aria-label={emojiLabel}
								aria-selected={selected}
								role="option"
							>
								{emoji}
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}
